/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from 'react';

import LoadingOlist from 'components/layout/loading-olist';
import useGoogleTagManager from 'hooks/use-google-tag-manager';
import { node } from 'prop-types';
import { useCookies } from 'react-cookie';
import { useSearchParam } from 'react-use';
import {
  CATEGORIES_URL,
  CREATE_PASSWORD_URL,
  ONBOARDING_FIRST_STEPS_URL,
  PERSONAL_INFORMATION_URL,
  REQUISITES_URL,
  REVENUES_URL,
  UNQUALIFIED_URL,
} from 'utils/urls';

import {
  LEAD_CATEGORY_STORAGE_NAME,
  LEAD_ID_COOKIE_NAME,
  LEAD_PROFILE_COOKIE_NAME,
  LEAD_REQUISITES_STORAGE_NAME,
  LEAD_REVENUE_ID_STORAGE_NAME,
  LEAD_REVENUE_STORAGE_NAME,
  SESSION_ID_STORAGE_NAME,
  SOURCE_APP_URL_PARAM,
  TOTAL_FIELDS_STORAGE_NAME,
  isMobileSource,
} from '../constants';

export const RootContext = React.createContext();

const generateAppUrls = (urls) => {
  const newUrls = {};

  Object.entries(urls).forEach(([key, value]) => {
    newUrls[key] = `${value}?source=app`;
  });

  return newUrls;
};

const currentDomain = window.location.hostname;

const COOKIE_DOMAIN = currentDomain.match(/localhost|olist\.io|olist\.com/) && currentDomain;
const COOKIE_DAYS_UNTIL_EXPIRE = 3;
const ONE_DAY_IN_SECONDS = 86400;
const COOKIE_MAX_AGE = COOKIE_DAYS_UNTIL_EXPIRE * ONE_DAY_IN_SECONDS;
const COOKIE_IS_SECURE = window.location.protocol === 'https:';
const COOKIE_OPTIONS = {
  path: '/',
  maxAge: COOKIE_MAX_AGE,
  domain: COOKIE_DOMAIN,
  secure: COOKIE_IS_SECURE,
};

const urls = {
  CATEGORIES_URL,
  REVENUES_URL,
  CREATE_PASSWORD_URL,
  UNQUALIFIED_URL,
  REQUISITES_URL,
  ONBOARDING_FIRST_STEPS_URL,
  PERSONAL_INFORMATION_URL,
};

const LeadsProvider = ({ children }) => {
  const [cookies, setCookie] = useCookies([
    LEAD_ID_COOKIE_NAME,
    LEAD_PROFILE_COOKIE_NAME,
    TOTAL_FIELDS_STORAGE_NAME,
    LEAD_CATEGORY_STORAGE_NAME,
    SESSION_ID_STORAGE_NAME,
  ]);

  const [appUrls, setAppUrls] = useState({});
  const sourceParam = useSearchParam('source');
  const isAppUrlsEmpty = !Object.keys(appUrls).length;
  const leadIdCookie = cookies[LEAD_ID_COOKIE_NAME];
  const sessionIdCookie = cookies[SESSION_ID_STORAGE_NAME];
  const leadProfileCookie = cookies[LEAD_PROFILE_COOKIE_NAME];
  const leadFieldCount = localStorage.getItem(TOTAL_FIELDS_STORAGE_NAME);
  const leadCategory = localStorage.getItem(LEAD_CATEGORY_STORAGE_NAME);
  const leadRevenue = localStorage.getItem(LEAD_REVENUE_STORAGE_NAME);
  const leadRequisites = localStorage.getItem(LEAD_REQUISITES_STORAGE_NAME);

  useGoogleTagManager({ isAdmin: 'false' });

  const setUrlsBySourceType = (sourceType) => {
    switch (sourceType) {
      case SOURCE_APP_URL_PARAM:
        return setAppUrls(generateAppUrls(urls));

      default:
        return setAppUrls(urls);
    }
  };

  const clearLeadFieldCountAndCategory = () => {
    localStorage.removeItem(LEAD_CATEGORY_STORAGE_NAME);
    localStorage.removeItem(TOTAL_FIELDS_STORAGE_NAME);
  };

  const setLeadTotalFields = (increase) => {
    const totalValue = localStorage.getItem(TOTAL_FIELDS_STORAGE_NAME);
    const lastTotal = totalValue ? parseInt(totalValue, 10) : 0;
    const totalLeadFields = lastTotal + parseInt(increase, 10);
    localStorage.setItem(TOTAL_FIELDS_STORAGE_NAME, totalLeadFields);
  };

  const setLeadRequisites = (requisites) => {
    localStorage.setItem(LEAD_REQUISITES_STORAGE_NAME, requisites);
  };

  const setLeadCategoryStorage = (category) => {
    localStorage.setItem(LEAD_CATEGORY_STORAGE_NAME, category);
  };

  const setLeadRevenueStorage = (revenue) => {
    localStorage.setItem(LEAD_REVENUE_STORAGE_NAME, revenue);
  };

  const setLeadRevenueIdStorage = (revenueId) => {
    localStorage.setItem(LEAD_REVENUE_ID_STORAGE_NAME, revenueId);
  };

  const setLeadIdCookie = (leadId) => {
    setCookie(LEAD_ID_COOKIE_NAME, leadId, COOKIE_OPTIONS);
  };
  const setSessionIdCookie = (leadId) => {
    setCookie(LEAD_ID_COOKIE_NAME, leadId, COOKIE_OPTIONS);
  };

  const setLeadProfileCookie = ({ createdAt, email, id, name }) => {
    const cookieValue = {
      id,
      name,
      email,
      createdAt,
    };
    setCookie(LEAD_PROFILE_COOKIE_NAME, cookieValue, COOKIE_OPTIONS);
  };

  const expireLeadCookiesAfterSeconds = (seconds = 10) => {
    // maxAge 10 was chosen just to delay a few seconds because of the redirect to another page
    const options = { ...COOKIE_OPTIONS, maxAge: seconds };
    setCookie(LEAD_ID_COOKIE_NAME, leadIdCookie, options);
    setCookie(LEAD_PROFILE_COOKIE_NAME, leadProfileCookie, options);
  };

  const callIntercomBoot = ({ createdAt, email, name }) => {
    window.OlistSupport?.boot({
      email,
      name,
      created_at: createdAt,
    });
  };

  useEffect(() => {
    setUrlsBySourceType(sourceParam);
  }, [sourceParam]);

  if (isAppUrlsEmpty) {
    return <LoadingOlist />;
  }
  return (
    <RootContext.Provider
      value={{
        appUrls,
        leadFieldCount,
        leadCategory,
        leadIdCookie,
        leadProfileCookie,
        sessionIdCookie,
        leadRevenue,
        leadRequisites,
        expireLeadCookiesAfterSeconds,
        isMobileWebview: isMobileSource(sourceParam),
        callIntercomBoot,
        clearLeadFieldCountAndCategory,
        setLeadTotalFields,
        setLeadCategoryStorage,
        setLeadRevenueStorage,
        setLeadRevenueIdStorage,
        setLeadIdCookie,
        setLeadProfileCookie,
        setLeadRequisites,
        setSessionIdCookie,
      }}
    >
      {children}
    </RootContext.Provider>
  );
};

LeadsProvider.propTypes = {
  children: node.isRequired,
};

export default LeadsProvider;
